import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AbstractSubmissionClosed, { useAbstractDeadline } from './AbstractSubmissionClosed';

const CustomEventAbstractMedical = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        confId: '',
        submissionType: ''
    });

    const [verifying, setVerifying] = useState(false);
    const [verifiedUser, setVerifiedUser] = useState(null);
    const [verificationError, setVerificationError] = useState('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [abstractDoc, setAbstractDoc] = useState(null);
    const { deadlinePassed, checking } = useAbstractDeadline();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            if (name === 'confId') {
                setVerifiedUser(null);
                setVerificationError('');
            }
            return { ...prev, [name]: name === 'confId' ? value.replace(/\s/g, '').toUpperCase() : value };
        });
    };

    const verifyConfId = async () => {
        if (!formData.confId) return;
        setVerifying(true);
        setVerificationError('');
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/verify-conference/${formData.confId}?type=abstract`);
            const data = await res.json();
            if (data.success && data.data) {
                setVerifiedUser(data.data);
            } else {
                setVerificationError(data.message || 'Invalid Conference Registration ID or not found.');
                setVerifiedUser(null);
            }
        } catch (err) {
            setVerificationError('Error verifying ID');
            setVerifiedUser(null);
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!verifiedUser) {
            alert('Please verify your Conference Registration ID before submitting.');
            return;
        }

        if (!formData.confId || !formData.submissionType) {
            alert('Please fill all required fields.');
            return;
        }

        if (!abstractDoc) {
            alert('Please upload an abstract document (PDF/Word).');
            return;
        }

        setLoading(true);
        try {
            const submitData = new FormData();
            submitData.append('confId', formData.confId);
            submitData.append('submissionType', formData.submissionType);
            submitData.append('fullName', verifiedUser.fullName || '');
            submitData.append('email', verifiedUser.email || '');
            submitData.append('contactNumber', verifiedUser.contactNumber || '');
            submitData.append('designation', verifiedUser.designation || '');
            submitData.append('institute', verifiedUser.organization || verifiedUser.college || '');
            submitData.append('state', verifiedUser.state || '');
            submitData.append('abstractDoc', abstractDoc);

            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/custom-events/abstract/medical/register`, {
                method: 'POST',
                body: submitData
            });
            const regData = await res.json();

            if (regData.success) {
                setSubmitted(true);
            } else {
                alert(regData.message || 'Submission failed');
            }
        } catch (error) {
            console.error(error);
            alert('An error occurred during submission');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#fcfaff] p-4 text-center">
                <div className="bg-white p-10 rounded-3xl shadow-xl max-w-lg w-full">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <span className="text-4xl">✅</span>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-800 mb-4 font-sans tracking-tight">Submission Successful!</h2>
                    <p className="text-gray-600 mb-8 max-w-sm mx-auto">Your abstract has been successfully recorded. Thank you for your submission.</p>
                    <button onClick={() => navigate('/event/jss/conference')}
                        className="bg-[#5928ed] hover:bg-[#4013b5] text-white px-8 py-3 rounded-full font-bold tracking-wide transition-all w-full">
                        Return to Event Page
                    </button>
                </div>
            </div>
        );
    }

    if (checking) return null;
    if (deadlinePassed) return <AbstractSubmissionClosed />;

    return (
        <div className="min-h-screen bg-[#fcfaff] py-12 px-4 sm:px-6 font-sans">
            <div className="max-w-3xl mx-auto space-y-8">

                {/* Header */}
                <div className="bg-gradient-to-br from-[#4013b5] to-[#7244f7] rounded-3xl p-8 sm:p-12 text-center text-white shadow-xl relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                    <div className="relative z-10">
                        <span className="uppercase tracking-[0.2em] font-bold text-xs opacity-80 bg-white/20 px-4 py-1.5 rounded-full inline-block mb-4">Abstract Submission</span>
                        <h1 className="text-3xl sm:text-4xl font-black mb-3 tracking-tight">MBBS Graduates</h1>
                        <p className="text-purple-100 text-lg max-w-xl mx-auto font-medium">Please verify your conference registration to proceed with submitting your abstract.</p>
                    </div>
                </div>

                {/* Form Wrapper */}
                <div className="bg-white rounded-3xl shadow-xl border border-gray-50 overflow-hidden">
                    <div className="bg-gray-50 px-8 py-5 border-b border-gray-100 flex items-center gap-3">
                        <span className="text-2xl">📝</span>
                        <h2 className="text-xl font-bold text-gray-800">Submission Details</h2>
                    </div>

                    <form onSubmit={handleSubmit} className="p-8 sm:p-10 space-y-8">

                        {/* ID Verification Section */}
                        <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100/50 space-y-4">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-blue-500">🛡️</span>
                                <label className="text-sm font-bold text-blue-900 uppercase tracking-wider">Your Conference Reg ID *</label>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <input
                                    type="text"
                                    name="confId"
                                    value={formData.confId}
                                    onChange={handleChange}
                                    placeholder="Ex: KAP-XXXX"
                                    disabled={verifiedUser || verifying}
                                    className="flex-1 bg-white border border-blue-200 rounded-xl px-4 py-3 text-gray-800 font-bold tracking-wider outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-400/20 uppercase"
                                />
                                {!verifiedUser ? (
                                    <button
                                        type="button"
                                        onClick={verifyConfId}
                                        disabled={!formData.confId || verifying}
                                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white px-8 py-3 rounded-xl font-bold tracking-wide transition-all shadow-md active:scale-95"
                                    >
                                        {verifying ? 'Verifying...' : 'Verify ID'}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => handleChange({ target: { name: 'confId', value: '' } })}
                                        className="bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 px-8 py-3 rounded-xl font-bold tracking-wide transition-all"
                                    >
                                        Change
                                    </button>
                                )}
                            </div>
                            {verificationError && <p className="text-red-500 text-sm font-bold bg-red-50 p-2 rounded-lg inline-block px-3">{verificationError}</p>}
                        </div>

                        {/* Verified User Details (Read-only) */}
                        {verifiedUser && (
                            <div className="bg-white border border-green-200 p-6 sm:p-8 rounded-3xl shadow flex flex-col sm:flex-row gap-6 items-start animate-fade-in relative overflow-hidden">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-full blur-3xl opacity-60 -mr-10 -mt-10 pointer-events-none"></div>
                                {/* Photo Section */}
                                {(verifiedUser.photo_url || verifiedUser.photoUrl) ? (
                                    <div className="shrink-0 w-32 h-32 sm:w-40 sm:h-40 bg-gray-100 rounded-xl overflow-hidden border border-gray-200 shadow-sm relative z-10">
                                        <img src={verifiedUser.photo_url || verifiedUser.photoUrl} alt="Participant" className="w-full h-full object-cover" />
                                    </div>
                                ) : (
                                    <div className="shrink-0 w-32 h-32 sm:w-40 sm:h-40 bg-green-50 rounded-xl flex items-center justify-center border border-green-100 shadow-sm relative z-10">
                                        <span className="text-4xl text-green-500">✅</span>
                                    </div>
                                )}

                                {/* Data Section */}
                                <div className="flex-1 w-full space-y-4 relative z-10">
                                    <div className="border-b border-gray-100 pb-3">
                                        <h3 className="text-2xl font-bold text-gray-900">{verifiedUser.fullName || verifiedUser.name}</h3>
                                        <p className="text-sm font-semibold text-[#5928ed]">{verifiedUser.registrationCategory || verifiedUser.category || 'Verified Participant'}</p>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-4 gap-x-4">
                                        {[
                                            { label: 'Registration ID', value: verifiedUser.registrationId || verifiedUser.confId },
                                            { label: 'Email', value: verifiedUser.email },
                                            { label: 'Contact Number', value: verifiedUser.contactNumber },
                                            { label: 'Registration Date', value: verifiedUser.createdAt ? new Date(verifiedUser.createdAt).toLocaleDateString('en-GB') : null },
                                            { label: 'Organization', value: verifiedUser.organization || verifiedUser.college || verifiedUser.institute },
                                            { label: 'Designation', value: verifiedUser.designation },
                                            { label: 'City', value: verifiedUser.city },
                                            { label: 'State', value: verifiedUser.state }
                                        ].map((field, idx) => field.value && field.value !== 'null' && field.value !== 'undefined' ? (
                                            <div key={idx}>
                                                <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">{field.label}</p>
                                                <p className="text-gray-900 text-sm font-semibold truncate" title={String(field.value)}>{String(field.value)}</p>
                                            </div>
                                        ) : null)}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Abstract Details */}
                        {verifiedUser && (
                            <div className="space-y-6 animate-fade-in">
                                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest border-b pb-2">Submission Details</h3>

                                <div className="space-y-4">
                                    <label className="text-[11px] font-bold text-gray-500 uppercase tracking-widest ml-1">Submission Type *</label>
                                    <div className="flex flex-wrap gap-4">
                                        {[{ value: 'oral-paper', label: 'Oral Paper' }, { value: 'poster', label: 'Poster' }].map(({ value, label }) => (
                                            <label key={value} className={`flex-1 flex items-center justify-center p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.submissionType === value ? 'bg-[#f5f0fc] border-[#5928ed] text-[#5928ed] shadow-sm' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}>
                                                <input
                                                    type="radio"
                                                    name="submissionType"
                                                    value={value}
                                                    checked={formData.submissionType === value}
                                                    onChange={handleChange}
                                                    className="hidden"
                                                />
                                                <span className="font-bold text-sm tracking-wide">{label} Presentation</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2 pt-2">
                                    <label className="text-[11px] font-bold text-[#5928ed] uppercase tracking-[0.15em] ml-1">Upload Abstract (PDF/MSWord) *</label>
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx"
                                        onChange={(e) => setAbstractDoc(e.target.files[0])}
                                        required
                                        className="w-full bg-white border border-dashed border-[#5928ed]/40 hover:border-[#5928ed] rounded-xl px-4 py-6 text-gray-800 text-sm font-medium transition-all duration-200 outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-[#fcfaff] file:text-[#5928ed] cursor-pointer"
                                    />
                                    <p className="text-[10px] text-gray-400 ml-1 font-medium mt-1">Maximum file size: 5MB</p>
                                </div>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="pt-6">
                            <button
                                type="submit"
                                disabled={loading || !verifiedUser}
                                className={`w-full py-4 rounded-xl font-bold tracking-widest uppercase transition-all shadow-lg text-white
                                    ${loading || !verifiedUser ? 'bg-gray-300 shadow-none cursor-not-allowed' : 'bg-gradient-to-r from-[#5928ed] to-[#7244f7] hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0'}`}
                            >
                                {loading ? 'Submitting...' : 'Submit '}
                            </button>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default CustomEventAbstractMedical;
